import {
  LineChart,
  Line,
  PieChart,
  Pie,
  BarChart,
  Bar,
  RadialBarChart,
  RadialBar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { AnalyticsMetrics } from "@core/services/SupportAnalyticsService";

interface MetricCardProps {
  title: string;
  value: string | number;
  change: string;
  changeType: "positive" | "negative" | "neutral";
  icon: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  changeType,
  icon,
}) => {
  const getChangeColor = () => {
    switch (changeType) {
      case "positive":
        return "text-green-400";
      case "negative":
        return "text-red-400";
      default:
        return "text-gray-400";
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-400">{title}</h3>
        <span className="text-2xl">{icon}</span>
      </div>
      <div className="text-2xl font-bold text-white mb-1">{value}</div>
      <div className={`text-sm ${getChangeColor()}`}>{change}</div>
    </div>
  );
};

const getTicketTypeColor = (type: string) => {
  switch (type) {
    case "bug":
      return "#EF4444";
    case "question":
      return "#3B82F6";
    case "feature":
      return "#10B981";
    case "feedback":
      return "#F59E0B";
    case "other":
      return "#6B7280";
    default:
      return "#6B7280";
  }
};

const getResponseTimeData = (metrics: AnalyticsMetrics) => {
  if (!metrics || metrics.totalTickets === 0) {
    return [
      { severity: "Critique", responseTime: 0 },
      { severity: "Élevée", responseTime: 0 },
      { severity: "Normale", responseTime: 0 },
      { severity: "Basse", responseTime: 0 },
    ];
  }

  // Données simulées basées sur les vraies tickets
  return [
    { severity: "Critique", responseTime: metrics.averageResponseTime * 1.5 },
    { severity: "Élevée", responseTime: metrics.averageResponseTime },
    { severity: "Normale", responseTime: metrics.averageResponseTime * 0.8 },
    { severity: "Basse", responseTime: metrics.averageResponseTime * 0.5 },
  ];
};

const getTicketRateData = (metrics: AnalyticsMetrics) => {
  if (!metrics || metrics.totalTickets === 0) {
    return [{ name: "Ticket Rate", value: 0, fill: "#10B981" }];
  }

  return [
    {
      name: "Ticket Rate",
      value: Math.min(metrics.ticketRate, 100), // Limiter à 100 pour le gauge
      fill:
        metrics.ticketRate < 20
          ? "#10B981"
          : metrics.ticketRate < 50
            ? "#F59E0B"
            : "#EF4444",
    },
  ];
};

export const AnalyticsCharts: React.FC<{
  metrics: AnalyticsMetrics | null;
  loading: boolean;
}> = ({ metrics, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="bg-white/5 border border-white/10 rounded-xl p-6"
          >
            <div className="animate-pulse">
              <div className="h-4 bg-gray-700 rounded mb-4"></div>
              <div className="h-8 bg-gray-700 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!metrics) {
    return (
      <div
        className="bg-white/5 border border-white/10 rounded-xl p-6 text-gray-300"
        data-testid="analytics-empty"
      >
        Aucune donnée analytique disponible pour le moment.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Métriques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Tickets"
          value={metrics.totalTickets}
          change={metrics.totalTickets > 0 ? "+12%" : "N/A"}
          changeType={metrics.totalTickets > 0 ? "positive" : "neutral"}
          icon="📊"
        />
        <MetricCard
          title="Tickets Ouverts"
          value={metrics.openTickets}
          change={metrics.totalTickets > 0 ? "-5%" : "N/A"}
          changeType={metrics.totalTickets > 0 ? "positive" : "neutral"}
          icon="🔄"
        />
        <MetricCard
          title="Taux SLA"
          value={`${metrics.slaComplianceRate.toFixed(1)}%`}
          change={metrics.totalTickets > 0 ? "+2%" : "N/A"}
          changeType={metrics.totalTickets > 0 ? "positive" : "neutral"}
          icon="⏰"
        />
        <MetricCard
          title="Satisfaction"
          value={`${metrics.satisfactionScore.toFixed(1)}`}
          change={
            metrics.feedbackCount > 0
              ? `${metrics.feedbackCount} avis réels`
              : "Score simulé"
          }
          changeType={metrics.feedbackCount > 0 ? "positive" : "neutral"}
          icon="⭐"
        />
      </div>

      {/* Graphiques Recharts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Line Chart : Tickets par jour */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            Évolution quotidienne
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={metrics.dailyStats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="date"
                stroke="#9CA3AF"
                tickFormatter={(value) =>
                  new Date(value).toLocaleDateString("fr", { weekday: "short" })
                }
              />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1F2937",
                  border: "1px solid #374151",
                }}
                labelStyle={{ color: "#F3F4F6" }}
                itemStyle={{ color: "#F3F4F6" }}
                formatter={(value: any, name: string) => [
                  value,
                  name === "tickets"
                    ? "Tickets"
                    : name === "resolved"
                      ? "Résolus"
                      : "En retard",
                ]}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="tickets"
                stroke="#60A5FA"
                strokeWidth={2}
                dot={{ fill: "#60A5FA", r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="resolved"
                stroke="#10B981"
                strokeWidth={2}
                dot={{ fill: "#10B981", r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="overdue"
                stroke="#EF4444"
                strokeWidth={2}
                dot={{ fill: "#EF4444", r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart : Répartition par type */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            Répartition par type
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={metrics.ticketTypeDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ type, percentage }) => `${type} ${percentage}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {metrics.ticketTypeDistribution.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getTicketTypeColor(entry.type)}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Graphiques secondaires */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart : Temps de réponse par sévérité */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            Temps de réponse par sévérité
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={getResponseTimeData(metrics)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="severity" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1F2937",
                  border: "1px solid #374151",
                }}
                labelStyle={{ color: "#F3F4F6" }}
                itemStyle={{ color: "#F3F4F6" }}
                formatter={(value: any) => [`${value}h`, "Temps moyen"]}
              />
              <Bar dataKey="responseTime" fill="#60A5FA" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Gauge : Ticket Rate vs Objectif */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            Ticket Rate vs Objectif
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <RadialBarChart
              cx="50%"
              cy="50%"
              innerRadius="30%"
              outerRadius="90%"
              data={getTicketRateData(metrics)}
            >
              <RadialBar dataKey="value" cornerRadius={10} fill="#8884d8" />
              <Legend />
              <Tooltip />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-400">
              Objectif: &lt;20/1000 | Actuel: {metrics.ticketRate}/1000
            </p>
            <p className="text-xs text-gray-500">
              {metrics.totalUsers} utilisateurs actifs
            </p>
          </div>
        </div>
      </div>

      {/* Métriques secondaires */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-2">
            Taux déflexion
          </h3>
          <div className="text-2xl font-bold text-white">
            {metrics.deflectionRate.toFixed(1)}%
          </div>
          <p className="text-sm text-gray-400 mt-1">
            Tickets résolus sans intervention
          </p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-2">
            Auto-résolution
          </h3>
          <div className="text-2xl font-bold text-white">
            {metrics.autoResolutionRate.toFixed(1)}%
          </div>
          <p className="text-sm text-gray-400 mt-1">Résolus automatiquement</p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-2">
            Tickets Fantômes
          </h3>
          <div className="text-2xl font-bold text-white">
            {metrics.ghostResolutions}
          </div>
          <p className="text-sm text-gray-400 mt-1">Résolutions invisibles</p>
        </div>
      </div>

      {/* Distribution des avis de satisfaction */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            Distribution des avis
          </h3>
          {metrics.feedbackCount === 0 && (
            <span className="text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded">
              Aucun avis collecté
            </span>
          )}
        </div>
        {metrics.feedbackCount > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={metrics.feedbackDistribution}
              layout="vertical"
              margin={{ left: 8, right: 24 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#374151"
                horizontal={false}
              />
              <XAxis type="number" stroke="#9CA3AF" allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="rating"
                stroke="#9CA3AF"
                tickFormatter={(v) => `${"⭐".repeat(v)}`}
                width={80}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1F2937",
                  border: "1px solid #374151",
                }}
                labelStyle={{ color: "#F3F4F6" }}
                formatter={(value: any) => [value, "Avis"]}
                labelFormatter={(v) => `${v} étoile${v > 1 ? "s" : ""}`}
              />
              <Bar dataKey="count" fill="#A78BFA" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex flex-col items-center justify-center h-32 text-gray-500 text-sm">
            <span className="text-3xl mb-2">⭐</span>
            Les avis apparaîtront ici une fois que des tickets seront résolus.
          </div>
        )}
      </div>

      {/* Top problèmes */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Top problèmes</h3>
        <div className="space-y-2">
          {metrics.topProblems.slice(0, 5).map((problem, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
            >
              <div className="flex-1">
                <div className="text-white font-medium">{problem.subject}</div>
                <div className="text-sm text-gray-400">
                  {problem.count} tickets
                </div>
              </div>
              <div className="text-right">
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    problem.severity === "critical"
                      ? "bg-red-500/20 text-red-400"
                      : problem.severity === "high"
                        ? "bg-orange-500/20 text-orange-400"
                        : problem.severity === "normal"
                          ? "bg-blue-500/20 text-blue-400"
                          : "bg-gray-500/20 text-gray-400"
                  }`}
                >
                  {problem.severity}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
